import { useCallback } from 'react';
import { useEpisodeStore } from '@/state/episodeStore';
import { useSelectionStore } from '@/state/selectionStore';
import { useGenerationStore } from '@/state/generationStore';
import { createScene, generateSceneImage } from '@/services/scenesApi';
import { runMutation } from '@/state/saveQueue';
import type { Scene } from '@/types';

interface GenerateInput {
  prompt: string;
  references: { sceneIds: string[]; castIds: string[] };
  fresh: boolean;
}

/**
 * Generates an image into the active scene. If no scene is active, appends a
 * fresh scene at the end of the grid first — this is what lets the user create
 * the very first scene without first picking an empty cell, AND what lets
 * `BottomInput` fire-and-forget multiple parallel single-image generations:
 * each fire that has no active selection just creates its own scene.
 */
export function useGenerateImage() {
  return useCallback(async (episodeUuid: string, input: GenerateInput) => {
    const { activeSceneId } = useSelectionStore.getState();
    const { scenes, upsertScene, upsertImage } = useEpisodeStore.getState();
    const { markInFlight, clearInFlight } = useGenerationStore.getState();

    let target: Scene | undefined = activeSceneId
      ? scenes.find((s) => s.id === activeSceneId)
      : undefined;

    if (!target) {
      // Optimistic insert first so concurrent fire-and-forget calls each
      // claim a unique `order` (without it, both reads of the live list
      // would return the same nextOrder and we'd get duplicate-order
      // scenes). The temp row is replaced with the server response below.
      const live = useEpisodeStore.getState().scenes;
      const nextOrder = (live.at(-1)?.order ?? 0) + 1;
      const tempId = `tmp-${crypto.randomUUID()}`;
      const tempScene: Scene = {
        id: tempId,
        episodeUuid,
        order: nextOrder,
        prompt: input.prompt,
        references: input.references,
        imageIds: [],
        videoIds: [],
        heroImageId: null,
        status: 'generating',
        version: 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        deletedAt: null,
      };
      upsertScene(tempScene);

      try {
        target = await runMutation(
          {
            episodeUuid,
            endpoint: '/v2/scenes',
            method: 'POST',
            body: { prompt: input.prompt, order: nextOrder, references: input.references },
          },
          () =>
            createScene(episodeUuid, {
              prompt: input.prompt,
              order: nextOrder,
              references: input.references,
            }),
        );
      } catch (err) {
        // Roll back the optimistic temp scene so the grid doesn't show a
        // ghost row that will never become real.
        useEpisodeStore.getState().removeScene(tempId);
        throw err;
      }

      // Swap temp row → real row.
      useEpisodeStore.getState().removeScene(tempId);
      upsertScene({ ...target, status: 'generating' });
      // Intentionally NOT activating — fire-and-forget should leave the
      // user's selection alone so they can keep stacking gens.
    }

    const previousStatus = target.status;
    upsertScene({ ...target, status: 'generating' });
    markInFlight(target.id);

    try {
      const newImage = await runMutation(
        {
          episodeUuid,
          endpoint: `/v2/scenes/${target.id}/images`,
          method: 'POST',
          body: input,
        },
        () => generateSceneImage(episodeUuid, target!.id, input),
      );

      upsertImage(newImage);
      const latest = useEpisodeStore.getState().scenes.find((s) => s.id === target!.id) ?? target;
      upsertScene({
        ...latest,
        status: 'ready',
        imageIds: [...latest.imageIds, newImage.id],
        version: latest.version + 1,
        updatedAt: Date.now(),
      });
      return newImage;
    } catch (err) {
      const latest = useEpisodeStore.getState().scenes.find((s) => s.id === target!.id) ?? target;
      upsertScene({ ...latest, status: previousStatus });
      throw err;
    } finally {
      clearInFlight(target.id);
    }
  }, []);
}
